/**
 * GPU 资源登记表（DESIGN.md §2.8）。
 *
 * 上下文丢失（切换显卡、系统休眠、驱动重启、标签页被浏览器回收显存）在真实
 * 使用中并不罕见，而 GPU 上的一切在那一刻全部作废。§2.8 的要求是：
 * **所有 GPU 资源都要能从 CPU 端的权威数据重建**，否则用户会丢掉整个作品。
 *
 * 强制手段是类型：{@link ResourceRegistry.register} 要求同时给出 `recreate()`，
 * 漏给在编译期就过不去，而不是等到某台机器上真的丢了上下文才发现无法恢复。
 *
 * 本文件不依赖任何后端特有类型——恢复框架对 WebGPU 的 device lost 与
 * WebGL2 的 context lost 是同一套。
 */

/**
 * 一份可重建的 GPU 资源。
 *
 * `T` 是资源句柄类型（纹理、程序……）。`recreate` 必须能在**没有旧句柄**的
 * 情况下重新造出等价资源——上下文丢失后旧句柄连读都不能读。
 */
export interface RecreatableResource<T> {
  /** 诊断用标签，出现在恢复日志里。 */
  readonly label: string;
  /**
   * 从 CPU 端权威数据重建资源。
   *
   * **不得**捕获旧句柄做增量恢复：上下文丢失后旧对象全部失效，
   * 任何"基于旧值"的重建都是在读已经不存在的东西。
   */
  recreate(): T;
}

/** 已登记资源的句柄。持有者通过 `current` 拿到当前有效的底层对象。 */
export interface RegisteredResource<T> {
  readonly label: string;
  /** 当前有效的底层句柄。恢复后会指向新对象，持有者不需要重新取。 */
  readonly current: T;
  /** 该资源被重建过多少次。用于诊断与测试断言。 */
  readonly recreateCount: number;
  /** 从登记表注销并释放。 */
  release(): void;
}

class Entry<T> implements RegisteredResource<T> {
  #value: T;
  #recreateCount = 0;
  #released = false;

  constructor(
    readonly label: string,
    private readonly factory: () => T,
    private readonly onRelease: (entry: Entry<T>) => void,
    private readonly disposer: ((value: T) => void) | undefined,
  ) {
    this.#value = factory();
  }

  get current(): T {
    if (this.#released) throw new Error(`资源「${this.label}」已释放。`);
    return this.#value;
  }

  get recreateCount(): number {
    return this.#recreateCount;
  }

  get released(): boolean {
    return this.#released;
  }

  /** 由登记表在恢复流程中调用。 */
  recreate(): void {
    if (this.#released) return;
    this.#value = this.factory();
    this.#recreateCount++;
  }

  release(): void {
    if (this.#released) return;
    this.#released = true;
    // 丢失上下文后释放旧句柄会抛错，但资源本来就没了，忽略即可。
    try {
      this.disposer?.(this.#value);
    } catch {
      /* 上下文已失效，无需处理 */
    }
    this.onRelease(this);
  }
}

export interface RegisterOptions<T> {
  readonly label: string;
  /** 创建（以及重建）资源。见 {@link RecreatableResource.recreate} 的约束。 */
  recreate(): T;
  /** 可选的释放回调。上下文丢失时不会被调用——那时旧句柄已经无效。 */
  dispose?(value: T): void;
}

/**
 * 资源登记表。
 *
 * 注册顺序即重建顺序：后注册的资源可能依赖先注册的（例如 framebuffer 依赖纹理），
 * 用插入序重建能让这种依赖自然满足。
 */
export class ResourceRegistry {
  readonly #entries = new Set<Entry<unknown>>();

  /**
   * 登记一份资源并立即创建。
   *
   * 类型签名强制 `recreate` 存在——这正是"所有 GPU 资源都能重建"这条约束的
   * 落实点，漏写在编译期就报错。
   */
  register<T>(options: RegisterOptions<T>): RegisteredResource<T> {
    const entry = new Entry<T>(
      options.label,
      options.recreate,
      (e) => this.#entries.delete(e as Entry<unknown>),
      options.dispose,
    );
    this.#entries.add(entry as Entry<unknown>);
    return entry;
  }

  /** 当前登记的资源数。 */
  get size(): number {
    return this.#entries.size;
  }

  /** 全部资源的标签，按注册顺序。 */
  labels(): string[] {
    return [...this.#entries].map((e) => e.label);
  }

  /**
   * 重建全部资源。
   *
   * 单个资源重建失败不会中断整体——其余资源仍应尽力恢复，失败清单一并抛出，
   * 好过在第一个错误处停下、留下一半是新一半是废句柄的登记表。
   */
  recreateAll(): { recreated: number; failures: { label: string; error: unknown }[] } {
    const failures: { label: string; error: unknown }[] = [];
    let recreated = 0;
    for (const entry of this.#entries) {
      try {
        entry.recreate();
        recreated++;
      } catch (error) {
        failures.push({ label: entry.label, error });
      }
    }
    return { recreated, failures };
  }

  /** 释放全部资源。 */
  releaseAll(): void {
    for (const entry of [...this.#entries]) entry.release();
    this.#entries.clear();
  }
}
